import { Component } from '@theme/component';

/** Topic navigation around native, merchant-editable accordion blocks. */
class UrcrownFaq extends Component {
  #signature = '';
  #topics = [];
  #activeId = '';
  #listeners;
  #frame;

  connectedCallback() {
    super.connectedCallback();
    this.#frame = requestAnimationFrame(() => this.#initialise());
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialise();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    cancelAnimationFrame(this.#frame);
    this.#signature = '';
  }

  #initialise() {
    const container = this.querySelector('[ref="panels"]');
    const navigation = this.querySelector('[ref="navigation"]');
    const select = this.querySelector('[ref="select"]');
    if (!container || !navigation || !(select instanceof HTMLSelectElement)) return;

    const panels = [...container.children].filter((child) => child.querySelector('h2'));
    const signature = panels.map((panel) => `${panel.getAttribute('data-shopify-editor-block') || ''}:${panel.querySelector('h2')?.textContent}:${[...panel.querySelectorAll('summary')].map((summary) => summary.textContent.trim()).join('|')}`).join('::');
    if (signature === this.#signature && this.hasAttribute('data-enhanced')) return;
    this.#signature = signature;
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.#topics = [];
    navigation.replaceChildren();
    select.replaceChildren();
    if (!panels.length) {
      this.removeAttribute('data-enhanced');
      return;
    }

    panels.forEach((panel, index) => {
      const heading = panel.querySelector('h2');
      const label = heading.textContent.trim();
      panel.id ||= `${this.id}-topic-${index + 1}`;
      panel.classList.add('uch-faq__panel');
      const headingBlock = heading.closest('.uch-faq__panel-heading') || heading.closest('.text-block') || heading;
      headingBlock.classList.add('uch-faq__panel-heading');
      let headingCount = headingBlock.querySelector('.uch-faq__count');
      if (!headingCount) {
        headingCount = document.createElement('span');
        headingCount.className = 'uch-faq__count';
        headingCount.setAttribute('aria-hidden', 'true');
        headingBlock.append(headingCount);
      }
      const questions = [...panel.querySelectorAll('details')];
      const count = String(questions.length).padStart(2, '0');
      headingCount.textContent = count;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'uch-faq__topic';
      button.dataset.topic = panel.id;
      button.setAttribute('aria-controls', panel.id);
      const buttonText = document.createElement('span');
      buttonText.textContent = label;
      const buttonCount = document.createElement('span');
      buttonCount.className = 'uch-faq__count';
      buttonCount.setAttribute('aria-hidden', 'true');
      buttonCount.textContent = count;
      button.append(buttonText, buttonCount);
      navigation.append(button);
      const option = document.createElement('option');
      option.value = panel.id;
      option.textContent = label;
      select.append(option);
      this.#topics.push({ panel, button, label });

      questions.forEach((details, questionIndex) => {
        if (!details.id) {
          const question = details.querySelector('summary')?.textContent.trim() || String(questionIndex + 1);
          const slug = question.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          let id = `faq-${slug}`;
          if (document.getElementById(id) && document.getElementById(id) !== details) id += `-${index + 1}-${questionIndex + 1}`;
          details.id = id;
        }
        details.addEventListener('toggle', () => {
          if (details.open && !panel.hidden && !Shopify.designMode) {
            history.replaceState(null, '', `${location.pathname}${location.search}#${details.id}`);
          }
        }, { signal });
      });
    });

    this.setAttribute('data-enhanced', '');
    navigation.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('button[data-topic]');
      if (button && navigation.contains(button)) this.#activate(button.dataset.topic, true);
    }, { signal });
    select.addEventListener('change', () => this.#activate(select.value, true), { signal });
    window.addEventListener('hashchange', () => this.#showLinkedQuestion(), { signal });
    document.addEventListener('shopify:block:select', (event) => {
      if (!(event.target instanceof Element) || !this.contains(event.target)) return;
      const topic = this.#topics.find(({ panel }) => panel.contains(event.target));
      if (!topic) return;
      this.#activate(topic.panel.id, false);
      const details = event.target.closest('details');
      if (details) details.open = true;
    }, { signal });
    const initialId = this.#topics.some(({ panel }) => panel.id === this.#activeId) ? this.#activeId : this.#topics[0].panel.id;
    this.#activate(initialId, false);
    this.#showLinkedQuestion();
  }

  #activate(id, updateHistory) {
    const topic = this.#topics.find(({ panel }) => panel.id === id);
    if (!topic) return;
    this.#activeId = id;
    for (const item of this.#topics) {
      const active = item === topic;
      item.panel.hidden = !active;
      item.button.setAttribute('aria-pressed', String(active));
    }
    const select = this.querySelector('[ref="select"]');
    if (select instanceof HTMLSelectElement) select.value = id;
    const status = this.querySelector('[ref="status"]');
    if (status && updateHistory) status.textContent = topic.label;
    if (updateHistory && !Shopify.designMode) history.replaceState(null, '', `${location.pathname}${location.search}#${id}`);
  }

  #showLinkedQuestion() {
    let id;
    try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
    if (!id) return;
    const target = document.getElementById(id);
    if (!target || !this.contains(target)) return;
    const topic = this.#topics.find(({ panel }) => panel === target || panel.contains(target));
    if (!topic) return;
    this.#activate(topic.panel.id, false);
    const details = target instanceof HTMLDetailsElement ? target : target.closest('details');
    if (details) details.open = true;
    if (!Shopify.designMode) {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
    }
  }
}

if (!customElements.get('urcrown-faq')) customElements.define('urcrown-faq', UrcrownFaq);
